import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { couponService } from '../../services/api'
import { Coupon, CouponValidation, COUPON_TYPES } from '../../models/Coupon.model'

const initialState = {
  coupons: {},
  loading: false,
  error: null,
  stats: null,
  validationResult: null,
  applicationResult: null,
  generatedCode: null
}

// Função para converter Coupon para objeto serializável
const couponToSerializable = (coupon) => {
  if (!coupon) return null
  
  // Se já é um objeto plano, retornar como está
  if (!(coupon instanceof Coupon)) {
    return coupon
  }
  
  // Converter Coupon para objeto plano (apenas dados, sem funções)
  const serializableCoupon = {
    id: coupon.id,
    code: coupon.code,
    description: coupon.description,
    type: coupon.type,
    value: coupon.value,
    minimumAmount: coupon.minimumAmount,
    maximumDiscount: coupon.maximumDiscount,
    usageLimit: coupon.usageLimit,
    userLimit: coupon.userLimit,
    usedCount: coupon.usedCount,
    validFrom: coupon.validFrom,
    validUntil: coupon.validUntil,
    valid_until: coupon.validUntil, // Alias para compatibilidade
    isActive: coupon.isActive,
    is_active: coupon.isActive, // Alias para compatibilidade
    createdBy: coupon.createdBy,
    createdAt: coupon.createdAt,
    updatedAt: coupon.updatedAt,
    usages: coupon.usages ? coupon.usages.map(usage => ({
      id: usage.id,
      userId: usage.userId,
      caseId: usage.caseId,
      originalAmount: usage.originalAmount,
      discountAmount: usage.discountAmount,
      finalAmount: usage.finalAmount,
      createdAt: usage.createdAt
    })) : [],
    applicableCategories: coupon.applicableCategories || [],
    applicableProducts: coupon.applicableProducts || []
  }
  
  return serializableCoupon
}

// Helper para métodos do cupom (não serializável)
export const couponHelpers = {
  isValid: (coupon) => {
    const now = new Date()
    
    if (!coupon.isActive) return false
    
    if (coupon.validFrom && now < new Date(coupon.validFrom)) return false
    if (coupon.validUntil && now > new Date(coupon.validUntil)) return false
    
    if (coupon.usageLimit > 0 && coupon.usedCount >= coupon.usageLimit) return false
    
    return true
  },
  
  canBeUsedBy: (coupon, userId) => {
    if (!couponHelpers.isValid(coupon)) return false
    
    if (coupon.userLimit > 0) {
      const userUsages = coupon.usages.filter(usage => usage.userId === userId)
      if (userUsages.length >= coupon.userLimit) return false
    }
    
    return true
  },
  
  getTypeLabel: (coupon) => {
    switch (coupon.type) {
      case COUPON_TYPES.PERCENTAGE:
        return 'Percentual'
      case COUPON_TYPES.FIXED_AMOUNT:
        return 'Valor Fixo'
      case COUPON_TYPES.QUANTITY:
        return 'Unitário'
      default:
        return 'Percentual'
    }
  },
  
  getValueLabel: (coupon) => {
    switch (coupon.type) {
      case COUPON_TYPES.PERCENTAGE:
        return `${coupon.value}%`
      case COUPON_TYPES.FIXED_AMOUNT:
        return `R$ ${parseFloat(coupon.value).toFixed(2)}`
      case COUPON_TYPES.QUANTITY:
        return `${coupon.value} unidades`
      default:
        return `${coupon.value}%`
    }
  },
  
  getDiscountLabel: (coupon) => {
    return couponHelpers.getValueLabel(coupon)
  },
  
  isPercentage: (coupon) => {
    return coupon.type === COUPON_TYPES.PERCENTAGE
  },
  
  isQuantity: (coupon) => {
    return coupon.type === COUPON_TYPES.QUANTITY
  },
  
  getStatusLabel: (coupon) => {
    if (!coupon.isActive) return 'Inativo'
    if (couponHelpers.isExpired(coupon)) return 'Expirado'
    if (coupon.usageLimit > 0 && coupon.usedCount >= coupon.usageLimit) return 'Esgotado'
    return 'Ativo'
  },
  
  getStatusColor: (coupon) => {
    if (!coupon.isActive) return 'secondary'
    if (couponHelpers.isExpired(coupon)) return 'danger'
    if (coupon.usageLimit > 0 && coupon.usedCount >= coupon.usageLimit) return 'warning'
    return 'success'
  },
  
  getUsageLabel: (coupon) => {
    if (coupon.usageLimit === 0) return `${coupon.usedCount}`
    return `${coupon.usedCount}/${coupon.usageLimit}`
  },
  
  getUsagePercentage: (coupon) => {
    if (coupon.usageLimit === 0) return 0
    return (coupon.usedCount / coupon.usageLimit) * 100
  },
  
  hasUsageLimit: (coupon) => {
    return coupon.usageLimit > 0
  },
  
  getRemainingUsage: (coupon) => {
    if (coupon.usageLimit === 0) return 'Ilimitado'
    return coupon.usageLimit - coupon.usedCount
  },
  
  getRemainingUsages: (coupon) => {
    if (coupon.usageLimit === 0) return 'Ilimitado'
    return coupon.usageLimit - coupon.usedCount
  },
  
  isExpired: (coupon) => {
    if (!coupon.validUntil) return false
    return new Date() > new Date(coupon.validUntil)
  },
  
  isExpiringSoon: (coupon, days = 7) => {
    if (!coupon.validUntil) return false
    const expiryDate = new Date(coupon.validUntil)
    const warningDate = new Date()
    warningDate.setDate(warningDate.getDate() + days)
    return expiryDate <= warningDate
  },
  
  isNotYetValid: (coupon) => {
    if (!coupon.validFrom) return false
    return new Date() < new Date(coupon.validFrom)
  },
  
  isUsageLimitReached: (coupon) => {
    return coupon.usageLimit > 0 && coupon.usedCount >= coupon.usageLimit
  },
  
  getFormattedValidUntil: (coupon) => {
    if (!coupon.validUntil) return 'Sem expiração'
    return new Date(coupon.validUntil).toLocaleDateString('pt-BR')
  },
  
  getDaysUntilExpiry: (coupon) => {
    if (!coupon.validUntil) return null
    const now = new Date()
    const expiry = new Date(coupon.validUntil)
    const diffTime = expiry - now
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  },
  
  calculateDiscount: (coupon, amount) => {
    if (!couponHelpers.isValid(coupon) || amount < coupon.minimumAmount) {
      return 0
    }
    
    let discount = 0
    
    if (coupon.type === COUPON_TYPES.PERCENTAGE) {
      discount = (amount * coupon.value) / 100
      
      if (coupon.maximumDiscount > 0) {
        discount = Math.min(discount, coupon.maximumDiscount)
      }
    } else if (coupon.type === COUPON_TYPES.FIXED_AMOUNT) {
      discount = coupon.value
    }
    
    return Math.min(discount, amount)
  }
}

// Listar cupons
export const fetchCoupons = createAsyncThunk(
  'coupons/fetchCoupons',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await couponService.getAll(filters)
      if (response.data && response.data.obj && response.data.obj.coupons) {
        return response.data.obj.coupons.map(c => {
          const coupon = Coupon.FromObj(c)
          return couponToSerializable(coupon)
        })
      }
      return []
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar cupons')
    }
  }
)

// Obter cupom específico
export const fetchCoupon = createAsyncThunk(
  'coupons/fetchCoupon',
  async (id, { rejectWithValue }) => {
    try {
      const response = await couponService.getById(id)
      if (response.data && response.data.obj) {
        const coupon = Coupon.FromObj(response.data.obj)
        return couponToSerializable(coupon)
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar cupom')
    }
  }
)

// Criar cupom
export const createCoupon = createAsyncThunk(
  'coupons/createCoupon',
  async (couponData, { rejectWithValue }) => {
    try {
      const response = await couponService.create(couponData)
      if (response.data && response.data.obj) {
        const coupon = Coupon.FromObj(response.data.obj)
        return couponToSerializable(coupon)
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao criar cupom')
    }
  }
)

// Atualizar cupom
export const updateCoupon = createAsyncThunk(
  'coupons/updateCoupon',
  async ({ id, couponData }, { rejectWithValue }) => {
    try {
      const response = await couponService.update(id, couponData)
      if (response.data && response.data.obj) {
        const coupon = Coupon.FromObj(response.data.obj)
        return couponToSerializable(coupon)
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao atualizar cupom')
    }
  }
)

// Excluir cupom
export const deleteCoupon = createAsyncThunk(
  'coupons/deleteCoupon',
  async (id, { rejectWithValue }) => {
    try {
      const response = await couponService.delete(id)
      return { id, message: response.data?.message || 'Cupom excluído com sucesso' }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao excluir cupom')
    }
  }
)

// Validar cupom
export const validateCoupon = createAsyncThunk(
  'coupons/validateCoupon',
  async (validationData, { rejectWithValue }) => {
    try {
      const response = await couponService.validate(validationData)
      if (response.data && response.data.obj) {
        return CouponValidation.FromObj(response.data.obj)
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Cupom inválido')
    }
  }
)

// Aplicar cupom
export const applyCoupon = createAsyncThunk(
  'coupons/applyCoupon',
  async (applicationData, { rejectWithValue }) => {
    try {
      const response = await couponService.apply(applicationData)
      if (response.data && response.data.obj) {
        return CouponValidation.FromObj(response.data.obj)
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao aplicar cupom')
    }
  }
)

// Gerar código de cupom
export const generateCouponCode = createAsyncThunk(
  'coupons/generateCouponCode',
  async (_, { rejectWithValue }) => {
    try {
      const response = await couponService.generateCode()
      if (response.data && response.data.obj) {
        return response.data.obj.code
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao gerar código')
    }
  }
)

// Ativar / desativar cupom
export const toggleCouponActive = createAsyncThunk(
  'coupons/toggleCouponActive',
  async (id, { rejectWithValue }) => {
    try {
      const response = await couponService.toggleActive(id)
      if (response.data && response.data.obj) {
        const coupon = Coupon.FromObj(response.data.obj)
        return couponToSerializable(coupon)
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao alterar status do cupom')
    }
  }
)

// Obter estatísticas
export const fetchCouponStats = createAsyncThunk(
  'coupons/fetchCouponStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await couponService.getStats()
      if (response.data && response.data.obj) {
        return response.data.obj
      }
      return null
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar estatísticas')
    }
  }
)

const couponsSlice = createSlice({
  name: 'coupons',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearValidationResult: (state) => {
      state.validationResult = null
    },
    clearApplicationResult: (state) => {
      state.applicationResult = null
    },
    clearGeneratedCode: (state) => {
      state.generatedCode = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Listar cupons
      .addCase(fetchCoupons.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCoupons.fulfilled, (state, action) => {
        state.loading = false
        state.coupons = {}
        action.payload.forEach(coupon => {
          state.coupons[coupon.id] = coupon
        })
      })
      .addCase(fetchCoupons.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Obter cupom específico
      .addCase(fetchCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCoupon.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.coupons[action.payload.id] = action.payload
        }
      })
      .addCase(fetchCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Criar cupom
      .addCase(createCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createCoupon.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.coupons[action.payload.id] = action.payload
        }
      })
      .addCase(createCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Atualizar cupom
      .addCase(updateCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCoupon.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload) {
          state.coupons[action.payload.id] = action.payload
        }
      })
      .addCase(updateCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Excluir cupom
      .addCase(deleteCoupon.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCoupon.fulfilled, (state, action) => {
        state.loading = false
        delete state.coupons[action.payload.id]
      })
      .addCase(deleteCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Validar cupom
      .addCase(validateCoupon.pending, (state) => {
        state.loading = true
        state.error = null
        state.validationResult = null
      })
      .addCase(validateCoupon.fulfilled, (state, action) => {
        state.loading = false
        state.validationResult = action.payload
      })
      .addCase(validateCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.validationResult = null
      })
      
      // Aplicar cupom
      .addCase(applyCoupon.pending, (state) => {
        state.loading = true
        state.error = null
        state.applicationResult = null
      })
      .addCase(applyCoupon.fulfilled, (state, action) => {
        state.loading = false
        state.applicationResult = action.payload
        
        // Atualizar o cupom no estado se ele existir
        if (action.payload && action.payload.coupon) {
          state.coupons[action.payload.coupon.id] = action.payload.coupon
        }
      })
      .addCase(applyCoupon.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.applicationResult = null
      })
      
      // Gerar código
      .addCase(generateCouponCode.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(generateCouponCode.fulfilled, (state, action) => {
        state.loading = false
        state.generatedCode = action.payload
      })
      .addCase(generateCouponCode.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Toggle active (sem loading global — não pisca a tabela)
      .addCase(toggleCouponActive.fulfilled, (state, action) => {
        if (action.payload) {
          state.coupons[action.payload.id] = action.payload
        }
      })
      .addCase(toggleCouponActive.rejected, (state, action) => {
        state.error = action.payload
      })

      // Estatísticas
      .addCase(fetchCouponStats.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCouponStats.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload
      })
      .addCase(fetchCouponStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  clearError,
  clearValidationResult,
  clearApplicationResult,
  clearGeneratedCode
} = couponsSlice.actions

export default couponsSlice.reducer